import { redirectToSilentAuth } from "../auth/auth";

export type CloudDrawing = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  size: number;
};

type CloudListResponse = {
  drawings: CloudDrawing[];
};

type CloudDrawingResponse = {
  drawing: CloudDrawing;
  payload: string;
};

const API_ROOT = "/api/cloud";

export class CloudConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CloudConflictError";
  }
}

const readResponse = async <T>(response: Response): Promise<T> => {
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // keep the original HTTP status error below
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      redirectToSilentAuth();
    }

    if (response.status === 409) {
      throw new CloudConflictError(
        body?.error || "Cloud copy is newer than the local view",
      );
    }

    throw new Error(
      body?.error || `Cloud storage request failed (${response.status})`,
    );
  }

  return body as T;
};

export const listCloudDrawings = async () => {
  const response = await fetch(`${API_ROOT}/drawings`, {
    credentials: "same-origin",
  });
  return (await readResponse<CloudListResponse>(response)).drawings;
};

export const getCloudDrawing = async (id: string) => {
  const response = await fetch(`${API_ROOT}/drawings/${id}`, {
    credentials: "same-origin",
  });
  return readResponse<CloudDrawingResponse>(response);
};

const CLIENT_ID_HEADER = "X-Excaliduck-Client-Id";

export const saveCloudDrawing = async ({
  id,
  name,
  payload,
  expectedUpdatedAt,
  clientId,
}: {
  id?: string | null;
  name: string;
  payload: string;
  // Optimistic concurrency hint. If set on PUT, server rejects with 409 when
  // the stored drawing's updatedAt differs (i.e., another client wrote it
  // after this client's last sync).
  expectedUpdatedAt?: string | null;
  // Identifies the originating tab. The server echoes it in SSE broadcasts so
  // subscribers can filter out updates they themselves caused.
  clientId?: string | null;
}) => {
  const response = await fetch(
    id ? `${API_ROOT}/drawings/${id}` : `${API_ROOT}/drawings`,
    {
      method: id ? "PUT" : "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(clientId ? { [CLIENT_ID_HEADER]: clientId } : {}),
      },
      body: JSON.stringify(
        expectedUpdatedAt && id
          ? { name, payload, expectedUpdatedAt }
          : { name, payload },
      ),
    },
  );
  return (await readResponse<{ drawing: CloudDrawing }>(response)).drawing;
};

export const deleteCloudDrawing = async (
  id: string,
  clientId?: string | null,
) => {
  const response = await fetch(`${API_ROOT}/drawings/${id}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: {
      ...(clientId ? { [CLIENT_ID_HEADER]: clientId } : {}),
    },
  });
  await readResponse<{ ok: true }>(response);
};

export type CloudUpdateEvent =
  | { type: "ready"; id: string; updatedAt: string }
  | {
      type: "update";
      id: string;
      updatedAt: string;
      name: string;
      size: number;
      originClientId: string | null;
    }
  | { type: "deleted"; id: string; originClientId: string | null };

export type CloudSubscription = {
  close: () => void;
};

// SSE subscription via fetch+streaming. EventSource would have been easier,
// but it does not support Authorization headers, and we don't want to leak
// PATs/JWTs into query strings or server logs.
//
// The handler is invoked with each parsed event (ready / update / deleted).
// Connection errors trigger an exponential backoff reconnect; the returned
// `close()` permanently aborts the subscription.
export const subscribeToCloudUpdates = (
  drawingId: string,
  onEvent: (event: CloudUpdateEvent) => void,
  onError?: (error: unknown) => void,
): CloudSubscription => {
  let controller: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let stopped = false;

  const parseAndDispatch = (raw: string) => {
    let event = "message";
    let data = "";
    for (const line of raw.split("\n")) {
      if (line.startsWith(":")) {
        continue;
      }
      if (line.startsWith("event: ")) {
        event = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        data += (data ? "\n" : "") + line.slice(6);
      }
    }
    if (!data) {
      return;
    }
    try {
      const payload = JSON.parse(data);
      if (event === "ready" || event === "update" || event === "deleted") {
        onEvent({ type: event, ...payload } as CloudUpdateEvent);
      }
    } catch (error) {
      onError?.(error);
    }
  };

  const scheduleReconnect = () => {
    if (stopped) {
      return;
    }
    const delay = Math.min(30000, 1000 * 2 ** Math.min(attempts, 5));
    attempts += 1;
    reconnectTimer = setTimeout(connect, delay);
  };

  const connect = async () => {
    if (stopped) {
      return;
    }
    controller = new AbortController();
    try {
      const response = await fetch(`${API_ROOT}/drawings/${drawingId}/events`, {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        if (response.status === 401 || response.status === 403) {
          redirectToSilentAuth();
          return;
        }
        throw new Error(`Subscribe failed (${response.status})`);
      }

      attempts = 0;
      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();
      let buffer = "";
      // SSE frames are separated by a blank line ("\n\n"). Collect data, split,
      // dispatch each complete frame; the trailing partial chunk stays in
      // `buffer` until the next read.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += value;
        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          if (frame.trim()) {
            parseAndDispatch(frame);
          }
          boundary = buffer.indexOf("\n\n");
        }
      }
      // Stream closed cleanly — server restart or proxy timeout. Reconnect.
      scheduleReconnect();
    } catch (error: any) {
      if (controller?.signal.aborted) {
        return;
      }
      onError?.(error);
      scheduleReconnect();
    }
  };

  connect();

  return {
    close: () => {
      stopped = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      controller?.abort();
    },
  };
};
